import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, Bell, SlidersHorizontal, Clock8, ArrowUpRight, Shield, Database, Play, Pause, Settings2, BarChart3, ListChecks, NotebookPen, Network, ChevronRight, ChevronDown, Filter, Search } from "lucide-react";

/**
 * ExecuWise – Minimal Demo UI
 * Single-file React prototype focused on clarity and problem‑solving flows.
 *
 * Pages: Dashboard, Worklist, Notifications, Policy Studio, Connectors, Analytics, Settings
 * Notes:
 * - Tailwind is assumed available in this runtime (no import required)
 * - All data is static/mock for demo purposes
 */

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: BarChart3 },
  { key: "worklist", label: "Worklist", icon: ListChecks },
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "policy", label: "Policy Studio", icon: NotebookPen },
  { key: "connectors", label: "Connectors", icon: Network },
  { key: "analytics", label: "Analytics", icon: BarChart3 },
  { key: "settings", label: "Settings", icon: Settings2 },
];

const SYSTEMS = [
  { id: "salesforce", name: "Salesforce", color: "#1B96FF" },
  { id: "jira", name: "Jira", color: "#0052CC" },
  { id: "zendesk", name: "Zendesk", color: "#03363D" },
  { id: "asana", name: "Asana", color: "#F06A6A" },
  { id: "monday", name: "monday.com", color: "#03C875" },
  { id: "todo", name: "Microsoft To Do", color: "#2563EB" },
];

const MOCK_ITEMS = [
  {
    id: "T-9211",
    title: "Follow up: Renewal – Acme Corp",
    system: "salesforce",
    owner: "Dana Levi",
    priority: "High",
    due: "Today 16:00",
    sla: "4h",
    risk: "Overdue",
    hops: 1,
  },
  {
    id: "J-3842",
    title: "Customer bug: Payment fails on Safari",
    system: "jira",
    owner: "Oren Kaplan",
    priority: "Critical",
    due: "In 1h (BH)",
    sla: "2h",
    risk: "Breach likely",
    hops: 0,
  },
  {
    id: "Z-7710",
    title: "VIP ticket: Latency spikes EU-West",
    system: "zendesk",
    owner: "Maya Azulay",
    priority: "Critical",
    due: "Now",
    sla: "1h",
    risk: "Due now",
    hops: 2,
  },
  {
    id: "A-1921",
    title: "Project: API rollout – reminder for QA",
    system: "asana",
    owner: "Ido Cohen",
    priority: "Medium",
    due: "Tomorrow",
    sla: "24h",
    risk: "On track",
    hops: 0,
  },
];

const KPI = [
  { label: "Execution Score", value: 87, delta: -2 },
  { label: "At‑risk Items", value: 11, delta: +3 },
  { label: "% on‑time", value: 94.2, delta: +1.1 },
  { label: "Predicted Breaches", value: 4, delta: -1 },
];

function Badge({ children, tone = "slate" }) {
  const tones = {
    teal: "bg-teal-50 text-teal-700 ring-1 ring-teal-200",
    red: "bg-rose-50 text-rose-700 ring-1 ring-rose-200",
    amber: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
    slate: "bg-slate-50 text-slate-700 ring-1 ring-slate-200",
  };
  return <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${tones[tone]}`}>{children}</span>;
}

function Header({ page, setPage }) {
  return (
    <header className="sticky top-0 z-40 bg-white/70 backdrop-blur border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
        {/* Logo */}
        <div className="flex items-center gap-2 pr-3">
          <img src="/images/image 2 (2).svg" alt="ExecuWise Logo" className="w-8 h-8" />
          <span className="font-extrabold tracking-tight text-slate-900">Execu<span className="text-teal-600">Wise</span></span>
          <Badge tone="slate">Demo</Badge>
        </div>

        {/* Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {NAV.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setPage(key)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition hover:bg-slate-100 ${
                page === key ? "bg-slate-100 text-slate-900" : "text-slate-600"
              }`}
            >
              <Icon size={16} /> {label}
            </button>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <button className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-100"><Search size={16}/>Search</button>
          <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800"><Play size={16}/>Open Worklist</button>
        </div>
      </div>
    </header>
  );
}

function SidebarFilters() {
  return (
    <aside className="w-full md:w-72 shrink-0 md:border-r border-slate-200 md:min-h-[calc(100vh-64px)] p-4">
      <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Filter size={16}/>Filters</h3>
      <div className="space-y-4">
        <Select label="Systems" options={["All", ...SYSTEMS.map(s => s.name)]} />
        <Select label="Team" options={["All", "Sales", "Support", "Engineering", "Operations"]} />
        <Select label="Priority" options={["All", "Critical", "High", "Medium", "Low"]} />
        <Select label="Time" options={["Today", "7 days", "30 days", "Custom"]} />
        <div className="flex items-center justify-between border rounded-lg p-3">
          <div>
            <div className="text-sm font-medium text-slate-700">Business Hours</div>
            <div className="text-xs text-slate-500">Use org calendars</div>
          </div>
          <Toggle />
        </div>
        <button className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white rounded-lg py-2.5 hover:bg-slate-800"><SlidersHorizontal size={16}/>Apply</button>
      </div>
    </aside>
  );
}

function Dashboard() {
    return (
    <div className="p-4 md:p-6 w-full">
      {/* Snapshot */}
      <div className="grid md:grid-cols-4 gap-4">
        {KPI.map((k) => (
          <div key={k.label} className="rounded-2xl border border-slate-200 p-4 bg-white text-center">
            <div className="text-slate-500 text-sm">{k.label}</div>
            <div className="flex items-end gap-2 mt-1 justify-center">
              <div className="text-3xl font-extrabold text-slate-900">{k.value}{k.label.includes('%') ? '' : ''}</div>
              <Badge tone={k.delta >= 0 ? "teal" : "red"}>{k.delta >= 0 ? `+${k.delta}` : k.delta}</Badge>
            </div>
            <div className="mt-4 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-teal-500" style={{ width: `${Math.min(100, Math.max(10, k.value))}%` }} />
            </div>
          </div>
        ))}
        </div>
        
      {/* Problem‑solving band */}
      <section className="mt-6 grid lg:grid-cols-3 gap-4">
        <ActionCard
          icon={<AlertTriangle className="text-amber-500" size={18} />}
          title="11 items need attention"
          subtitle="Biggest risk: Jira → Customer Bugs (4 overdue)"
          cta="Open prioritized worklist"
        />
        <ActionCard
          icon={<Clock8 className="text-sky-600" size={18} />}
          title="4 predicted SLA breaches"
          subtitle="Escalate now to avoid penalties"
          cta="Escalate level 1"
        />
        <ActionCard
          icon={<Shield className="text-teal-600" size={18} />}
          title="Policies reduced time‑to‑close by 23%"
          subtitle="Top policy: VIP support (L1 → L2)"
          cta="Tune policies"
        />
      </section>

      {/* Worklist preview */}
      <section className="mt-8">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-slate-700">At‑Risk Worklist (top 8)</h3>
          <button className="text-sm text-teal-700 hover:text-teal-800">View all <ArrowUpRight className="inline" size={14}/></button>
        </div>
        <WorklistTable items={MOCK_ITEMS} compact />
      </section>
    </div>
  );
}

function WorklistTable({ items, compact = false }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <table className="w-full">
        <thead className="bg-slate-50 text-slate-600 text-xs">
          <tr>
            <th className="text-left p-3">Task</th>
            <th className="text-left p-3">System</th>
            <th className="text-left p-3">Owner</th>
            <th className="text-left p-3">Due</th>
            <th className="text-left p-3">SLA</th>
            <th className="text-left p-3">Risk</th>
            <th className="p-3"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50/60">
              <td className="p-3 whitespace-nowrap">
                <div className="font-medium text-slate-900">{it.title}</div>
                <div className="text-xs text-slate-500">{it.id}</div>
              </td>
              <td className="p-3"><SystemPill id={it.system}/></td>
              <td className="p-3 text-slate-700">{it.owner}</td>
              <td className="p-3 text-slate-700">{it.due}</td>
              <td className="p-3 text-slate-700">{it.sla}</td>
              <td className="p-3">{riskBadge(it.risk)}</td>
              <td className="p-3">
                <div className="flex gap-2 justify-end">
                  <GhostButton>Open</GhostButton>
                  <PrimaryButton>Nudge</PrimaryButton>
                  <GhostButton>Escalate</GhostButton>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function riskBadge(risk){
  const map = {
    "Due now": { t: "amber" },
    "Overdue": { t: "red" },
    "Breach likely": { t: "red" },
    "On track": { t: "teal" },
  };
  const tone = map[risk]?.t || "slate";
  return <Badge tone={tone}>{risk}</Badge>;
}

function Notifications() {
  const feed = [
    { id: 1, title: "Nudged Dana Levi (Salesforce)", body: "Follow‑up overdue • Escalates to L2 in 1h", time: "2m", tone: "teal" },
    { id: 2, title: "Predicted breach in Jira queue", body: "Payment bug risk increased • 3 items", time: "14m", tone: "amber" },
    { id: 3, title: "L2 Escalation sent to Support Manager", body: "VIP ticket still open past 1h", time: "29m", tone: "red" },
  ];
  return (
    <div className="p-4 md:p-6 w-full">
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <div className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Bell size={16}/>Notification Center</div>
          <GhostButton>Export</GhostButton>
        </div>
        <ul className="divide-y divide-slate-100">
          {feed.map((n)=> (
            <li key={n.id} className="p-4 flex items-start gap-3">
              <span className={`mt-1 w-2 h-2 rounded-full ${n.tone === 'red' ? 'bg-rose-500' : n.tone === 'amber' ? 'bg-amber-500' : 'bg-teal-500'}`} />
              <div className="flex-1">
                <div className="text-slate-900 font-medium">{n.title}</div>
                <div className="text-slate-500 text-sm">{n.body}</div>
              </div>
              <div className="text-xs text-slate-400">{n.time}</div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function PolicyStudio() {
  const policies = [
    { name: "VIP Support Escalation", status: "Active", rule: "If SLA < 2h and no ack → notify manager, then L2 in 1h", impact: 89 },
    { name: "Overdue Sales Follow‑up", status: "Active", rule: "If overdue > 1 BH day → escalate to L2", impact: 94 },
    { name: "Bug Queue Unassigned", status: "Testing", rule: "If unassigned > 4h → alert TL, notify CTO in 2h", impact: 76 },
  ];

  return (
    <div className="p-4 md:p-6 w-full space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">Policy Library</h3>
        <PrimaryButton>Create Policy</PrimaryButton>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        {policies.map((p)=> (
          <div key={p.name} className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-slate-900">{p.name}</div>
              <Badge tone={p.status === 'Active' ? 'teal' : 'amber'}>{p.status}</Badge>
            </div>
            <div className="text-sm text-slate-600 mt-2">Rule: {p.rule}</div>
            <div className="mt-4 flex items-center justify-between">
              <div className="text-slate-500 text-sm">Impact Score</div>
              <div className="text-xl font-extrabold text-slate-900">{p.impact}</div>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <PrimaryButton>Simulate</PrimaryButton>
              <GhostButton>Edit</GhostButton>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Connectors() {
  const connectors = [
    { id: "salesforce", name: "Salesforce", status: "Connected", last: "2m ago" },
    { id: "jira", name: "Jira", status: "Connected", last: "Just now" },
    { id: "zendesk", name: "Zendesk", status: "Warning", last: "12m ago" },
    { id: "asana", name: "Asana", status: "Connected", last: "3m ago" },
  ];
  return (
    <div className="p-4 md:p-6 w-full">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {connectors.map((c)=> (
          <div key={c.id} className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <SystemPill id={c.id}/>
                <div className="font-medium text-slate-900">{c.name}</div>
              </div>
              <Badge tone={c.status === 'Connected' ? 'teal' : 'amber'}>{c.status}</Badge>
            </div>
            <div className="text-sm text-slate-500 mt-2">Last sync: {c.last}</div>
            <div className="mt-4 flex gap-2">
              <PrimaryButton>Sync now</PrimaryButton>
              <GhostButton>Settings</GhostButton>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Analytics() {
  return (
    <div className="p-4 md:p-6 w-full space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="text-sm font-semibold text-slate-700">SLA Trend</div>
        <div className="mt-2 h-36 rounded-xl bg-gradient-to-r from-teal-100 to-slate-100" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-sm font-semibold text-slate-700">Escalation Funnel</div>
          <div className="mt-2 h-36 rounded-xl bg-gradient-to-b from-slate-100 to-teal-100" />
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-sm font-semibold text-slate-700">Hotspots</div>
          <ul className="mt-2 space-y-2 text-sm text-slate-700">
            <li>Jira → Customer Bugs <Badge tone="red">4 overdue</Badge></li>
            <li>Salesforce → Renewals <Badge tone="amber">2 due today</Badge></li>
            <li>Zendesk → VIP queue <Badge tone="amber">latency</Badge></li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function Settings() {
  return (
    <div className="p-4 md:p-6 w-full">
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="text-sm font-semibold text-slate-700 mb-3">Organization</div>
        <div className="grid md:grid-cols-2 gap-4">
          <Input label="Tenant name" placeholder="Acme Ltd" />
          <Input label="Default timezone" placeholder="Asia/Jerusalem" />
          <Input label="Business hours" placeholder="Sun–Thu 9:00–18:00" />
          <Input label="Escalation email" placeholder="ops@acme.com" />
        </div>
        <div className="mt-4 flex gap-2">
          <PrimaryButton>Save</PrimaryButton>
          <GhostButton>Cancel</GhostButton>
        </div>
        </div>
      </div>
    );
}

// —————————————————————— UI Primitives ——————————————————————

function Select({ label, options = [] }){
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <select className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500">
        {options.map(o => <option key={o}>{o}</option>)}
      </select>
    </label>
  );
}

function Input({ label, placeholder }){
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <input placeholder={placeholder} className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500"/>
    </label>
  );
}

function Toggle(){
  const [on, setOn] = useState(true);
  return (
    <button onClick={()=>setOn(!on)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${on ? 'bg-teal-600' : 'bg-slate-300'}`}>
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${on ? 'translate-x-5' : 'translate-x-1'}`} />
    </button>
  );
}

function PrimaryButton({ children }){
  return <button className="px-3 py-2 rounded-lg text-sm bg-teal-600 text-white hover:bg-teal-700">{children}</button>;
}

function GhostButton({ children }){
  return <button className="px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-100 border border-slate-200">{children}</button>;
}

function ActionCard({ icon, title, subtitle, cta }){
  return (
    <div className="rounded-2xl border border-slate-200 p-4 bg-white flex items-center justify-between">
      <div>
        <div className="text-slate-900 font-semibold flex items-center gap-2">{icon}{title}</div>
        <div className="text-sm text-slate-500 mt-1">{subtitle}</div>
      </div>
      <GhostButton>{cta}</GhostButton>
    </div>
  );
}

function SystemPill({ id }){
  const system = SYSTEMS.find(s=>s.id===id);
  if(!system) return null;
  return (
    <span className="inline-flex items-center gap-2 px-2 py-1 rounded-md ring-1 ring-slate-200 text-sm">
      <span className="w-2 h-2 rounded-full" style={{ background: system.color }} />
      <span className="text-slate-700">{system.name}</span>
    </span>
  );
}

// —————————————————————— Root ——————————————————————
export default function App(){
  const [page, setPage] = useState("dashboard");
  return (
    <div className="min-h-screen bg-slate-50">
      <Header page={page} setPage={setPage} />
      <div className="max-w-7xl mx-auto flex">
        <div className="hidden md:block"><SidebarFilters/></div>
        <main className="flex-1">
          {page === "dashboard" && <Dashboard/>}
          {page === "worklist" && <div className="p-4 md:p-6 w-full"><WorklistTable items={MOCK_ITEMS}/></div>}
          {page === "notifications" && <Notifications/>}
          {page === "policy" && <PolicyStudio/>}
          {page === "connectors" && <Connectors/>}
          {page === "analytics" && <Analytics/>}
          {page === "settings" && <Settings/>}
        </main>
      </div>
      <footer className="max-w-7xl mx-auto px-4 py-8 text-xs text-slate-400">© 2025 ExecuWise Demo UI</footer>
    </div>
  );
}
